import React from "react";
import { useTranslation } from "react-i18next";
import { Card, Table } from "react-bootstrap";

interface InventoryItem {
  ID: string;
  Qty: string;
  product_id?: string;
  quantity_done?: number;
  productName?: string;
  imageUrl?: string;
  currentStock?: number;
  seller_reference?: string;
}

interface ProductsListCardProps {
  items: InventoryItem[];
  title?: string;
}

const ProductsListCard: React.FC<ProductsListCardProps> = ({ 
  items, 
  title = "Product List" 
}) => {
  const { t } = useTranslation();
  return (
    <Card 
      className="mb-4 product-list-card"
      style={{
        borderRadius: "18px",
        background: "var(--bs-card-bg)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        overflow: "hidden",
        border: "1px solid var(--bs-border-color)"
      }}
    >
      <style>
        {`
          .product-list-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          }
          
          .product-image {
            transition: all 0.3s ease;
            border: 2px solid transparent;
          }
          
          .product-row:hover .product-image {
            transform: scale(1.05);
            border-color: var(--bs-primary);
          }
          
          .product-row {
            transition: all 0.2s ease;
            cursor: default;
          }
          
          .product-row:hover {
            background-color: rgba(var(--bs-primary-rgb), 0.03);
          }
          
          .product-badge {
            transition: all 0.2s ease;
            font-weight: 500;
          }
          
          .product-row:hover .badge-stock {
            background-color: rgba(var(--bs-secondary-rgb), 0.2) !important;
          }
          
          .product-row:hover .badge-qty {
            background-color: rgba(var(--bs-primary-rgb), 0.2) !important;
          }
          
          .product-table th {
            font-weight: 600;
            text-transform: uppercase;
            font-size: 0.8rem;
            letter-spacing: 0.5px;
            padding-top: 1rem;
            padding-bottom: 1rem;
            color: var(--bs-body-color-rgb);
          }
          
          .empty-state {
            padding: 3rem 1rem;
            color: var(--bs-body-color-rgb);
            font-style: italic;
          }
        `}
      </style>
      <Card.Header 
        className="border-0 py-3 px-4"
        style={{
          borderBottom: "1px solid rgba(0,0,0,0.06)"
        }}
      >
        <h5 className="mb-0 fw-semibold" style={{ fontSize: "1.2rem" }}>{title}</h5>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table className="table-hover mb-0 product-table">
            <thead className="bg-light">
              <tr>
                <th className="border-0 ps-4" style={{ width: "50%" }}>{t("inventory.product")}</th>
                <th className="border-0 text-center" style={{ width: "20%" }}>{t("inventory.currentStock")}</th>
                <th className="border-0 text-center" style={{ width: "20%" }}>{t("quantity")}</th>
              </tr>
            </thead>
            <tbody>
              {items.length > 0 ? (
                items.map((item, index) => (
                  <tr key={index} className="product-row">
                    <td className="py-3 ps-4">
                      <div className="d-flex align-items-center">
                        {item.imageUrl && (
                          <div 
                            className="product-image"
                            style={{ 
                              width: 50, 
                              height: 50, 
                              marginRight: 15, 
                              borderRadius: "10px", 
                              overflow: "hidden",
                              boxShadow: "0 4px 8px rgba(0,0,0,0.08)"
                            }}
                          >
                            <img
                              src={item.imageUrl}
                              alt={item.productName || "Product"}
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          </div>
                        )}
                        <div>
                          <div className="fw-semibold mb-1 text-body">
                            {item.productName || "Product " + (item.product_id || item.ID)}
                          </div>
                          <div className="text-body-secondary" style={{ fontSize: "0.85rem" }}>
                            {t("inventory.ref")}: {item.seller_reference || t("inventory.noReference")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 text-center">
                      <span className="badge badge-stock bg-secondary bg-opacity-10 text-secondary rounded-pill px-3 py-2 product-badge">
                        {item.currentStock !== undefined ? item.currentStock : '-'}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      <span className="badge badge-qty bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-2 product-badge">
                        {item.quantity_done || item.Qty}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="text-center empty-state">
                    {t("inventory.noProductsFound")}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ProductsListCard;
